import {
  forwardRef,
  useState,
  useRef,
  useCallback,
  useEffect,
  type TextareaHTMLAttributes,
  useId,
} from 'react';
import {
  baseInputStyles,
  inputSizeStyles,
  inputStateStyles,
  type BaseInputProps,
} from './fieldStyles';
import { FieldLabel } from './FieldLabel';

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement>,
    Omit<BaseInputProps, 'leftIcon' | 'rightIcon' | 'icon'> {
  /** Number of visible text rows */
  rows?: number;
  /** Enable auto-grow: textarea grows with content up to maxHeight */
  autoGrow?: boolean;
  /** Maximum height in pixels when autoGrow is enabled */
  maxGrowHeight?: number;
}

// ============================================================================
// Textarea Component
// ============================================================================

/**
 * Textarea component for multi-line text input
 *
 * @example
 * ```tsx
 * <Textarea
 *   label="Description"
 *   placeholder="Enter description..."
 *   rows={4}
 * />
 * ```
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helperText,
      required,
      inputSize = 'md',
      fullWidth = true,
      wrapperClassName = '',
      className = '',
      id: providedId,
      rows = 3,
      grayBackground = false,
      autoGrow = false,
      maxGrowHeight = 300,
      maxLength,
      onChange,
      value,
      defaultValue,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const textareaId = providedId ?? generatedId;
    const errorId = `${textareaId}-error`;
    const helperId = `${textareaId}-helper`;
    const internalRef = useRef<HTMLTextAreaElement | null>(null);
    const [charCount, setCharCount] = useState(0);

    const hasError = !!error;

    // Combine external and internal refs
    const setRefs = useCallback(
      (node: HTMLTextAreaElement | null) => {
        internalRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
        }
      },
      [ref]
    );

    // Auto-grow logic
    const adjustHeight = useCallback(() => {
      const textarea = internalRef.current;
      if (!textarea || !autoGrow) return;
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxGrowHeight)}px`;
    }, [autoGrow, maxGrowHeight]);

    // Initialize char count and auto-grow on mount / value change
    useEffect(() => {
      const textarea = internalRef.current;
      if (textarea) {
        setCharCount(textarea.value.length);
        adjustHeight();
      }
    }, [value, adjustHeight]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setCharCount(e.target.value.length);
        adjustHeight();
        onChange?.(e);
      },
      [onChange, adjustHeight]
    );

    const grayBgStyles = grayBackground
      ? 'bg-[var(--color-bg-secondary)] border-transparent focus:bg-[var(--color-bg-primary)] focus:border-accent-500'
      : '';

    const minHeight = 'min-h-[80px]';

    const textareaClasses = [
      baseInputStyles,
      inputSizeStyles[inputSize],
      grayBackground ? grayBgStyles : (hasError ? inputStateStyles.error : inputStateStyles.default),
      hasError && grayBackground ? 'border-[var(--color-error)]' : '',
      autoGrow ? `resize-none overflow-hidden ${minHeight}` : `resize-y ${minHeight}`,
      className,
    ]
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    const wrapperClasses = [
      fullWidth ? 'w-full' : '',
      wrapperClassName,
    ].join(' ');

    const showCharCounter = maxLength != null && maxLength > 0;
    const isOverLimit = showCharCounter && charCount > maxLength;

    /* Строка под полем рисовалась ВСЕГДА и при пустом содержимом добавляла свои `mt-1`. Пока в
       ней всегда стоял счётчик, это было незаметно; со снятым счётчиком поле получало четыре
       лишних пикселя ровно там, где ряд и выравнивается. */
    const errorText = hasError && typeof error === 'string' ? error : null;
    const helperLine = !hasError && helperText ? helperText : null;
    const hasNoteRow = Boolean(errorText || helperLine || showCharCounter);

    return (
      <div className={wrapperClasses}>
        <FieldLabel htmlFor={textareaId} label={label} required={required} />

        <textarea
          ref={setRefs}
          id={textareaId}
          className={textareaClasses}
          rows={rows}
          aria-invalid={hasError}
          aria-describedby={
            hasError ? errorId : helperText ? helperId : undefined
          }
          maxLength={maxLength}
          onChange={handleChange}
          value={value}
          defaultValue={defaultValue}
          {...props}
        />

        {/* Character counter and error/helper row */}
        {hasNoteRow && (
          <div className="flex items-start justify-between gap-2 mt-1">
            <div className="flex-1 min-w-0">
              {errorText && (
                <p id={errorId} className="text-sm text-[var(--color-error)]">
                  {errorText}
                </p>
              )}

              {helperLine && (
                <p id={helperId} className="text-sm text-[var(--color-text-secondary)]">
                  {helperLine}
                </p>
              )}
            </div>

            {showCharCounter && (
              <span
                className={`text-xs shrink-0 ${
                  isOverLimit
                    ? 'text-[var(--color-error)]'
                    : 'text-[var(--color-text-disabled)]'
                }`}
              >
                {charCount} / {maxLength}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
